import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DetailFieldConflict,
  DetailFieldLinkResolution,
  DueDateWhen,
  NewTaskDueDateInfo,
  useDateHelper,
  useProjectPlanService,
} from 'nekst-api';
import {
  ButtonColor,
  ButtonSize,
  Dropdown,
  FormTemplate,
  Right,
  SubmitButton,
} from 'ui-builder';
import InformationBox from '../../../shared/web/layout/InformationBox';
import { PreviousButton } from '../Buttons';
import useAdditionalWorkflowLaunchNavigator from './additionalWorkflowLaunchNavigator';
import AdditionalWorkflowSteps, { AdditionalWorkflowStepId } from './AdditionalWorkflowSteps';
import { useConflictsContext } from './AdditionalWorkflowLaunchWrapper';
import { useProjectContext } from '../../view/ProjectContext';
import useTasksNavigator from '../../view/tasks/tasksNavigator';

import styles from './AdditionalWorkflowConflictsPage.module.scss';

function whenToString(when: string) {
  switch (when) {
    case DueDateWhen.BEFORE_START_DATE:
      return 'before start date';
    case DueDateWhen.AFTER_START_DATE:
      return 'after start date';
    case DueDateWhen.BEFORE_END_DATE:
      return 'before end date';
    case DueDateWhen.AFTER_END_DATE:
      return 'after end date';
    default:
      return '';
  }
}

function formatDueDateInfo(info: NewTaskDueDateInfo): string {
  const getDaysString = (days: number) => (days === 1 ? 'day' : 'days');

  if (info.dueDateDays != null && info.dueDateWhen) {
    return `${info.dueDateDays} ${getDaysString(info.dueDateDays)} ${whenToString(info.dueDateWhen)}`;
  } else if (info.parentTaskDays != null && info.parentTaskName) {
    const word = info.parentTaskIsProceeding ? 'before' : 'after';
    return `${info.parentTaskDays} ${getDaysString(info.parentTaskDays)} ${word} ${info.parentTaskName}`;
  } else {
    return 'No Due Date';
  }
}

const RESOLUTION_OPTIONS = [
  { value: DetailFieldLinkResolution.KEEP_EXISTING, label: 'Keep Existing' },
  { value: DetailFieldLinkResolution.OVERWRITE, label: 'Overwrite' },
];

function ConflictRow(props: {
  conflict: DetailFieldConflict;
}) {
  const { conflict } = props;
  const tasksNavigator = useTasksNavigator();
  const dateHelper = useDateHelper();

  const handleExistingTaskClick = (e: React.MouseEvent) => {
    e.preventDefault();
    tasksNavigator.openTask(conflict.existingTaskId);
  };

  return (
    <tr>
      <td>{conflict.rootFieldName}</td>
      <td>
        <a
          href={tasksNavigator.getOpenTaskUri(conflict.existingTaskId)}
          onClick={handleExistingTaskClick}
        >
          {conflict.existingTaskName}
        </a>
        {` — ${conflict.existingFieldValue ? dateHelper.toString(dateHelper.parseYyyyMmDd(conflict.existingFieldValue)) : conflict.existingLinkedFieldName}`}
      </td>
      <td>
        <div>{conflict.newTaskName}</div>
        {conflict.newTaskDueDateInfo ? (
          <div className={styles.dueDateDescription}>
            {formatDueDateInfo(conflict.newTaskDueDateInfo)}
            {conflict.newTaskDueDateInfo.calculatedDueDate ? (
              <div>{dateHelper.toString(dateHelper.parseYyyyMmDd(conflict.newTaskDueDateInfo.calculatedDueDate))}</div>
            ) : null}
          </div>
        ) : null}
      </td>
      <td>
        <Dropdown<string>
          source={`resolution_${conflict.rootFieldId}`}
          options={RESOLUTION_OPTIONS}
          hideLabel
          isClearable={false}
        />
      </td>
    </tr>
  );
}

type ConflictResolutionFormData = Record<string, DetailFieldLinkResolution>;

export default function AdditionalWorkflowConflictsPage() {
  const navigator = useAdditionalWorkflowLaunchNavigator();
  const params = useParams();
  const projectPlanService = useProjectPlanService();
  const conflictsContext = useConflictsContext();
  const projectContext = useProjectContext();

  const projectId = parseInt(params.id!, 10);
  const planId = parseInt(params.planId!, 10);

  useEffect(() => {
    if (!conflictsContext.isLoading && !conflictsContext.hasConflicts) {
      navigator.openPartiesPage();
    }
  }, [conflictsContext.isLoading, conflictsContext.hasConflicts]);

  if (conflictsContext.isLoading) {
    return null;
  }

  const initialData: ConflictResolutionFormData = {};
  conflictsContext.conflicts.forEach((conflict) => {
    initialData[`resolution_${conflict.rootFieldId}`] = conflict.currentResolution;
  });

  const submitFunc = async (data: ConflictResolutionFormData) => {
    const taskResolutions: Record<number, DetailFieldLinkResolution> = {};
    conflictsContext.conflicts.forEach((conflict) => {
      const rootResolution = data[`resolution_${conflict.rootFieldId}`] || DetailFieldLinkResolution.KEEP_EXISTING;
      conflict.affectedNewTaskIds.forEach((taskId) => {
        taskResolutions[taskId] = rootResolution;
      });
    });

    await projectPlanService.setConflictResolutions(projectId, planId, taskResolutions);
    navigator.openPartiesPage();
  };

  return (
    <>
      <InformationBox
        text="The new workflow has detail boxes that conflict with existing values. Choose whether to keep the existing value or overwrite it with the new workflow's value for each detail box."
      />
      <AdditionalWorkflowSteps
        currentStep={AdditionalWorkflowStepId.CONFLICTS}
        hasConflicts
        hasGroups={projectContext.plan?.hasGroups}
      />
      <FormTemplate<ConflictResolutionFormData>
        submitFormFunc={submitFunc}
        initialData={initialData}
        buttons={null}
      >
        <div style={{ marginBottom: '2rem', overflow: 'hidden' }}>
          <PreviousButton onClick={navigator.openAllTasksPage} />
          <Right>
            <SubmitButton
              text="Next &nbsp;> "
              size={ButtonSize.BIG}
              color={ButtonColor.BLUE}
              isFilled
            />
          </Right>
        </div>
        <table className={styles.conflictsTable}>
          <thead>
            <tr>
              <th scope="col">Detail Box Name</th>
              <th scope="col">Current Value</th>
              <th scope="col">New Workflow Value</th>
              <th scope="col">Decision</th>
            </tr>
          </thead>
          <tbody>
            {conflictsContext.conflicts.map((conflict) => (
              <ConflictRow
                key={conflict.rootFieldId}
                conflict={conflict}
              />
            ))}
          </tbody>
        </table>
      </FormTemplate>
    </>
  );
}
